import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  TextField, 
  InputAdornment, 
  Button, 
  Chip, 
  Grid, 
  CircularProgress, 
  Alert,
  IconButton,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import ScienceIcon from '@mui/icons-material/Science';
import { marketplaceAPI } from '../../services/marketplaceAPI';
import { flashSaleService } from '../../services/flashSaleService';
import { useCart } from '../../contexts/CartContext';
import ProductCard from '../../components/marketplace/ProductCard';
import Pagination from '../../components/marketplace/Pagination';
import FlashSaleSection from '../../components/marketplace/FlashSaleSection';
import DashboardButton from '../../components/DashboardButton';
import muiTheme from '../../theme/muiTheme';
import '../../styles/marketplace.css';

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flashSalesLoading, setFlashSalesLoading] = useState(true);
  const [error, setError] = useState('');
  const [flashSalesError, setFlashSalesError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    page: 1,
    limit: 12,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchProducts();
    fetchFlashSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await marketplaceAPI.getProducts(filters);
      
      if (response.success) {
        setProducts(response.data.products);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to fetch products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Error loading products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlashSales = async () => {
    try {
      setFlashSalesLoading(true);
      setFlashSalesError('');
      
      const response = await flashSaleService.getActiveFlashSales();
      
      if (response.success) {
        setFlashSales(response.data.flashSales || []);
      } else {
        setFlashSalesError('Failed to fetch flash sales');
      }
    } catch (err) {
      console.error('Error fetching flash sales:', err);
      setFlashSalesError('Error loading flash sales.');
    } finally {
      setFlashSalesLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    handleFilterChange({ search: searchValue });
  };

  const handleCategoryChange = (category) => {
    handleFilterChange({ category });
  };

  const handleAddToCart = (product, flashSale = null) => {
    try {
      // Prepare flash sale data if available
      const flashSaleData = flashSale ? {
        salePrice: flashSale.salePrice,
        flashSaleId: flashSale._id,
        discountPercentage: flashSale.discountPercentage
      } : null;
      
      // Add to cart using the cart context
      addToCart(product._id, 1, null, flashSaleData);
      
      console.log('Added to cart:', {
        productId: product._id,
        flashSaleData
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const categories = [
    { value: '', label: 'All Products', icon: <ShoppingBagIcon />, color: 'primary' },
    { value: 'medicine', label: 'Medicines', icon: <LocalPharmacyIcon />, color: 'success' },
    { value: 'lab-test', label: 'Lab Tests', icon: <ScienceIcon />, color: 'info' }
  ];

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Dashboard Button */}
        <DashboardButton userRole="Patient" />
        
        {/* Header Section with Navy Blue Gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 30%, #2563EB 60%, #3B82F6 100%)',
            color: 'white',
            boxShadow: '0 10px 40px rgba(30, 64, 175, 0.25)',
            position: 'relative',
            overflow: 'hidden',
            pb: 6,
            pt: 4
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              opacity: 0.3,
              pointerEvents: 'none'
            }}
          />
          
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  mb: 2,
                  textShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  letterSpacing: '-0.02em',
                  color: '#FFFFFF'
                }}
              >
                Healthcare Marketplace
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.95,
                  fontWeight: 300,
                  maxWidth: '600px',
                  mx: 'auto',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Discover quality medicines and comprehensive lab tests from verified suppliers
              </Typography>
              
              {/* Search Section */}
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  p: 3,
                  maxWidth: '600px',
                  mx: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  mb: 3
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    opacity: 0.9
                  }}
                >
                  🔍 Search Products
                </Typography>
                
                <TextField
                  fullWidth
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleFilterChange({ search: searchTerm });
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleSearch('')}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 3,
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid rgba(255, 255, 255, 0.8)'
                      },
                      '& input': {
                        color: '#1E293B',
                        fontWeight: 500,
                        py: 1.5
                      }
                    }
                  }}
                />
              </Paper>

              {/* Category Filters */}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ gap: 1.5 }}
              >
                {categories.map((category) => (
                  <Chip
                    key={category.value}
                    icon={category.icon}
                    label={category.label}
                    onClick={() => handleCategoryChange(category.value)}
                    variant={filters.category === category.value ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor: filters.category === category.value 
                        ? 'rgba(255, 255, 255, 0.25)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      backdropFilter: 'blur(10px)',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      py: 2.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.25)',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                      },
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                  xl: 'repeat(5, 1fr)'
                },
                gap: 2
              }}
            >
              {[...Array(6)].map((_, index) => (
                <Paper
                  key={index}
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'grey.100',
                    height: { xs: 400, md: 350, lg: 320, xl: 300 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress />
                </Paper>
              ))}
            </Box>
          ) : products.length === 0 ? (
            // Empty State
            <Paper
              elevation={3}
              sx={{
                p: 8,
                textAlign: 'center',
                borderRadius: 3,
                bgcolor: 'background.paper'
              }}
            >
              <Typography variant="h2" sx={{ mb: 2, fontSize: '4rem' }}>
                🛍️
              </Typography>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                {filters.search || filters.category
                  ? 'No products found matching your criteria.'
                  : 'No products available.'}
              </Typography>
              {(filters.search || filters.category) && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setFilters(prev => ({
                      ...prev,
                      category: '',
                      search: '',
                      page: 1
                    }));
                    setSearchTerm('');
                  }}
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              )}
            </Paper>
          ) : (
            <>
              {/* Results Info */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
                  {Math.min(pagination.currentPage * filters.limit, pagination.totalProducts)} of{' '}
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {pagination.totalProducts}
                  </Box>{' '}
                  products
                </Typography>
                <Chip
                  label={`Page ${pagination.currentPage} of ${pagination.totalPages}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* Flash Sales Section */}
              <FlashSaleSection
                flashSales={flashSales}
                loading={flashSalesLoading}
                error={flashSalesError}
                onAddToCart={handleAddToCart}
              />

              {/* Products Grid - CSS Grid for better control on larger screens */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(5, 1fr)'
                  },
                  gap: 2,
                  mb: 4
                }}
              >
                {products.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </Box>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4
                  }}
                >
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    hasNextPage={pagination.hasNextPage}
                    hasPrevPage={pagination.hasPrevPage}
                  />
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default MarketplacePage;
